import { useState } from "react";
import { useNavigate } from "react-router";
import {
    Container,
    Typography,
    Button,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { Loading } from "src/components/utils/Loading";
import { useSnackbar } from "src/utils/useSnackbar";
import { useGetAllRoles } from "src/api/client-api/roles/useGetAllRoles.ts";
import { useRoleDelete } from "src/api/client-api/roles/useDeleteRole.ts";
import { URLHandler } from "src/services/URLHandler";
import { FoodsIcon, FoodsTooltip } from "@foodpilot/foods";
import { useNavigateSettingsParent } from "../useNavigateSettingsParent";
import { CREATE, DELETE, MODIFY, ROLE, useFoodpilotContext } from "src/context/FoodpilotContext";

export const RolesList = () => {
    const { allRoles } = useGetAllRoles();
    const { canUser } = useFoodpilotContext();
    const canCreateRoles = canUser(CREATE, ROLE);
    const canModifyRoles = canUser(MODIFY, ROLE);
    const canDeleteRoles = canUser(DELETE, ROLE);

    const snackbar = useSnackbar();
    const navigate = useNavigate();
    const navigateSettingsParent = useNavigateSettingsParent();
    const { t } = useTranslation();
    const deleteBrand = useRoleDelete();

    const [displayDialog, setDisplayDialog] = useState(false);
    const [idToDelete, setIdToDelete] = useState<string | undefined>(undefined);

    const handleDeleteClick = (id: string) => () => {
        setIdToDelete(id);
        setDisplayDialog(true);
    };

    const handleGoBack = () => {
        navigateSettingsParent();
    };

    const handleCreateRole = () => {
        navigate(URLHandler.settings.roles.create());
    };

    const handleEditRole = (roleCode: string) => {
        navigate(URLHandler.settings.roles.edit(roleCode));
    };

    if (allRoles === undefined) return <Loading />;

    return (
        <Container
            component="main"
            maxWidth={false}
            sx={{
                m: "0",
                p: "5rem !important",
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    width: "100%",
                    border: "1px solid #E7E6E4",
                    borderRadius: "16px",
                    overflow: "hidden",
                    backgroundColor: "white",
                    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.08)",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "32px",
                        borderBottom: "1px solid #E7E6E4",
                        backgroundColor: "white",
                    }}
                >
                    <Box
                        sx={{
                            width: "28px",
                            height: "28px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                        }}
                        onClick={handleGoBack}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                "& svg": {
                                    strokeWidth: "2px",
                                    stroke: "currentColor",
                                },
                            }}
                        >
                            <FoodsIcon icon="arrowLeftShort" size={6} />
                        </Box>
                    </Box>
                    <Box sx={{ marginLeft: "16px" }}>
                        <Typography variant="number-inter" sx={{ lineHeight: "1.6em", color: "#393A36" }}>
                            {t("roles")}
                        </Typography>
                    </Box>
                </Box>

                {/* Add Button Section */}
                {canCreateRoles && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            padding: "24px 32px",
                            gap: "12px",
                        }}
                    >
                        <Button
                            variant="contained"
                            startIcon={<FoodsIcon size={2} icon="plus" />}
                            onClick={handleCreateRole}
                            sx={(theme) => ({
                                borderRadius: "44px",
                                padding: "0px 20px",
                                height: "40px",
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                textTransform: "none",
                                background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                color: "white !important",
                                boxShadow: "0px 1px 2px 0px rgba(42, 43, 39, 0.02)",
                                "&:hover": {
                                    background: `linear-gradient(180deg, ${theme.palette.primary[600]} 0%, ${theme.palette.primary[700]} 100%)`,
                                    boxShadow: "0px 1px 3px 0px rgba(42, 43, 39, 0.03)",
                                    color: "white !important",
                                },
                                "&:disabled": {
                                    background: `linear-gradient(180deg, ${theme.palette.primary[500]} 0%, ${theme.palette.primary[600]} 100%)`,
                                    opacity: 0.36,
                                    color: "white !important",
                                },
                            })}
                        >
                            {t("settings.roles.addrole")}
                        </Button>
                    </Box>
                )}

                {/* Content area */}
                <Box sx={{ padding: "0px 32px 24px" }}>
                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                            border: "1px solid #E7E6E4",
                            borderRadius: "8px",
                            overflow: "hidden",
                        }}
                    >
                        <Table sx={{ "& .MuiTableCell-root": { border: "none" } }}>
                            <TableHead>
                                <TableRow
                                    sx={{
                                        backgroundColor: "#FEFCFB",
                                        "& .MuiTableCell-root": {
                                            borderBottom: "1px solid #E7E6E4",
                                            borderRight: "1px solid #E7E6E4",
                                            padding: "0px 14px 0px 20px",
                                            height: "48px",
                                            "&:last-child": {
                                                borderRight: "none",
                                            },
                                        },
                                    }}
                                >
                                    <TableCell>
                                        <Typography
                                            variant="small-medium"
                                            sx={{ lineHeight: "1.67em", color: "#767673" }}
                                        >
                                            {t("role")}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant="small-medium"
                                            sx={{ lineHeight: "1.67em", color: "#767673" }}
                                        >
                                            {t("permissions")}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: 120 }}>
                                        <Typography
                                            variant="small-medium"
                                            sx={{ lineHeight: "1.67em", color: "#767673" }}
                                        >
                                            {t("actions")}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allRoles.map((role, index) => {
                                    const totalPermissions = role.permission?.length || 0;

                                    return (
                                        <TableRow
                                            key={role.code}
                                            sx={{
                                                "& .MuiTableCell-root": {
                                                    borderRight: "1px solid #E7E6E4",
                                                    padding: "0px 14px 0px 20px",
                                                    height: "58px",
                                                    "&:last-child": {
                                                        borderRight: "none",
                                                        padding: "0px 8px",
                                                    },
                                                },
                                                "&:hover": {
                                                    backgroundColor: "#F9F9F8",
                                                },
                                            }}
                                        >
                                            <TableCell sx={{ borderBottom: "none" }}>
                                                <Stack direction="row" spacing={0.75} alignItems="center">
                                                    <Typography
                                                        variant="small-medium"
                                                        sx={{ lineHeight: "22px", color: "#1E1E1E" }}
                                                    >
                                                        {role.displayName}
                                                    </Typography>
                                                    {role.system && (
                                                        <FoodsTooltip
                                                            title={t(
                                                                "settings.roles.system_role_tooltip",
                                                                "System role: read-only",
                                                            )}
                                                            placement="top"
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    color: "#767673",
                                                                }}
                                                            >
                                                                <FoodsIcon icon="lock" size={1} />
                                                            </Box>
                                                        </FoodsTooltip>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: "none" }}>
                                                <Typography
                                                    variant="small-medium"
                                                    sx={{ lineHeight: "1.6em", color: "#393A36" }}
                                                >
                                                    {totalPermissions}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ borderBottom: "none" }}>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    {canModifyRoles && (
                                                        <Box
                                                            component="button"
                                                            onClick={() => handleEditRole(role.code)}
                                                            sx={{
                                                                width: "32px",
                                                                height: "32px",
                                                                borderRadius: "50%",
                                                                border: "none",
                                                                backgroundColor: "transparent",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                cursor: "pointer",
                                                                transition: "background-color 0.2s",
                                                                "&:hover": {
                                                                    backgroundColor: "#F5F5F5",
                                                                },
                                                            }}
                                                        >
                                                            <FoodsIcon icon="edit" size={1} />
                                                        </Box>
                                                    )}
                                                    {canDeleteRoles && (
                                                        <FoodsTooltip
                                                            title={
                                                                role.system ?
                                                                    t(
                                                                        "settings.roles.system_role_no_delete",
                                                                        "System roles cannot be deleted",
                                                                    )
                                                                :   ""
                                                            }
                                                            placement="top"
                                                        >
                                                            <Box
                                                                component="button"
                                                                disabled={role.system}
                                                                onClick={
                                                                    role.system ? undefined : (
                                                                        handleDeleteClick(role.code)
                                                                    )
                                                                }
                                                                sx={{
                                                                    width: "32px",
                                                                    height: "32px",
                                                                    borderRadius: "50%",
                                                                    border: "none",
                                                                    backgroundColor: "transparent",
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    cursor: role.system ? "not-allowed" : "pointer",
                                                                    opacity: role.system ? 0.36 : 1,
                                                                    transition: "background-color 0.2s",
                                                                    "&:hover": {
                                                                        backgroundColor:
                                                                            role.system ? "transparent" : "#F5F5F5",
                                                                    },
                                                                }}
                                                            >
                                                                <FoodsIcon icon="delete" size={1} />
                                                            </Box>
                                                        </FoodsTooltip>
                                                    )}
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Paper>

            {/* Delete Confirmation Dialog */}
            <Dialog maxWidth="xs" open={displayDialog}>
                <DialogTitle>{t("common.areyousure")}</DialogTitle>
                <DialogContent dividers>{t("settings.roles.deleterole")}</DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setDisplayDialog(false);
                        }}
                    >
                        {t("non")}
                    </Button>
                    <Button
                        onClick={() => {
                            if (idToDelete) {
                                deleteBrand.mutate(idToDelete, {
                                    onError: () => {
                                        snackbar.setSnackbarProps({
                                            message: "Unknown error...",
                                            type: "error",
                                        });
                                    },
                                });
                            }
                            setDisplayDialog(false);
                        }}
                        color="error"
                    >
                        {t("oui")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};
